import { createClientHelpers } from './generated/client-helpers.js';
export class HydraUIAPIError extends Error {
    status;
    statusText;
    body;
    response;
    headers;
    url;
    constructor(status, statusText, body, response) {
        super(`Journeys UI API error ${status}: ${statusText}`);
        this.status = status;
        this.statusText = statusText;
        this.body = body;
        this.name = 'HydraUIAPIError';
        this.response = response;
        this.headers = response?.headers;
        this.url = response?.url;
    }
}
/**
 * Generic fetch transport over an OpenAPI `paths`-shaped type. Instantiate it
 * with a spec's `paths` type to get type-safe `request`/`get`/`post`/… keyed on
 * that spec's routes (path params, request body, and response all inferred).
 *
 * The default UI API client is {@link HydraUIAPIClient} (generated `paths` +
 * the codegen'd namespaced helpers). Backends without an OpenAPI spec, such
 * as Pipelines (`/cdp/api`), pass their own hand-written `paths` types and use
 * the typed methods directly.
 */
export class APIClient {
    baseUrl;
    defaultFetchOptions;
    defaultHeaders;
    fetchFn;
    timeout;
    onRequestTiming;
    constructor(config = {}) {
        this.baseUrl = config.baseUrl ?? '';
        this.defaultFetchOptions = config.fetchOptions ?? {};
        this.defaultHeaders = config.headers;
        this.fetchFn = config.fetch;
        this.timeout = config.timeout ?? 30_000;
        this.onRequestTiming = config.onRequestTiming;
    }
    async request(method, path, options) {
        // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
        return (await this.dispatch(method, path, options));
    }
    async dispatch(method, path, options) {
        const startMs = performance.now();
        let status = 0;
        try {
            const responseType = options?.responseType ?? 'json';
            const headers = new Headers();
            await this.applyHeaders(headers, this.defaultHeaders);
            await this.applyHeaders(headers, options?.headers);
            this.setDefaultAcceptHeader(headers, responseType);
            const body = this.serializeBody(options);
            if (options?.body !== undefined && !headers.has('Content-Type')) {
                headers.set('Content-Type', 'application/json');
            }
            const requestInit = {
                ...this.defaultFetchOptions,
                ...options?.fetchOptions,
                body,
                headers,
                method,
            };
            if (options?.signal) {
                requestInit.signal = options.signal;
            }
            const response = await this.fetchWithTimeout(this.buildURL(path, options?.params), requestInit, options?.timeout);
            status = response.status;
            if (!response.ok) {
                throw await this.toAPIError(response);
            }
            return await this.parseResponse(response, responseType);
        }
        finally {
            this.reportRequestTiming(method, path, status, startMs);
        }
    }
    reportRequestTiming(method, path, status, startMs) {
        if (!this.onRequestTiming) {
            return;
        }
        try {
            this.onRequestTiming({ method, path, status, startMs, durationMs: performance.now() - startMs });
        }
        catch {
            // telemetry must never break a request
        }
    }
    get(path, options) {
        return this.request('GET', path, options);
    }
    post(path, options) {
        return this.request('POST', path, options);
    }
    put(path, options) {
        return this.request('PUT', path, options);
    }
    patch(path, options) {
        return this.request('PATCH', path, options);
    }
    delete(path, options) {
        return this.request('DELETE', path, options);
    }
    buildURL(path, params) {
        const baseUrl = typeof this.baseUrl === 'function' ? this.baseUrl() : this.baseUrl;
        const raw = joinURL(baseUrl.replace(/\/+$/, ''), this.resolvePath(path, params));
        const absolute = /^[A-Za-z][A-Za-z0-9+.-]*:\/\//.test(raw);
        const url = new URL(raw, 'http://hydra.local');
        this.applyQuery(url, params);
        return absolute ? url.toString() : `${url.pathname}${url.search}${url.hash}`;
    }
    resolvePath(path, params) {
        const pathParams = parameterSection(params, 'path');
        return path.replace(/\{([^}]+)\}/g, (_, key) => {
            const value = pathParams[key];
            if (value === undefined || value === null) {
                throw new Error(`missing path parameter: ${key}`);
            }
            return encodeURIComponent(String(value));
        });
    }
    applyQuery(url, params) {
        const query = parameterSection(params, 'query');
        for (const [key, value] of Object.entries(query)) {
            if (value === undefined || value === null) {
                continue;
            }
            if (Array.isArray(value)) {
                for (const item of value) {
                    if (item !== undefined && item !== null) {
                        url.searchParams.append(key, String(item));
                    }
                }
                continue;
            }
            url.searchParams.set(key, String(value));
        }
    }
    async fetchWithTimeout(input, init, timeoutOverride) {
        const fetchFn = this.fetchFn ?? globalThis.fetch;
        const timeout = timeoutOverride ?? this.timeout;
        if (!timeout) {
            return fetchFn(input, init);
        }
        const controller = new AbortController();
        const externalSignal = init.signal;
        const abortFromExternalSignal = () => controller.abort(externalSignal?.reason);
        const timer = setTimeout(() => controller.abort(), timeout);
        if (externalSignal?.aborted) {
            controller.abort(externalSignal.reason);
        }
        else {
            externalSignal?.addEventListener('abort', abortFromExternalSignal, { once: true });
        }
        try {
            return await fetchFn(input, { ...init, signal: controller.signal });
        }
        finally {
            clearTimeout(timer);
            externalSignal?.removeEventListener('abort', abortFromExternalSignal);
        }
    }
    async applyHeaders(headers, extra) {
        if (!extra) {
            return;
        }
        const resolved = typeof extra === 'function' ? await extra() : extra;
        new Headers(resolved).forEach((value, key) => headers.set(key, value));
    }
    serializeBody(options) {
        if (!options) {
            return undefined;
        }
        if (options.rawBody !== undefined && options.body !== undefined) {
            throw new Error('request options cannot include both body and rawBody');
        }
        if (options.rawBody !== undefined) {
            return options.rawBody;
        }
        return options.body === undefined ? undefined : JSON.stringify(options.body);
    }
    setDefaultAcceptHeader(headers, responseType) {
        if (headers.has('Accept')) {
            return;
        }
        headers.set('Accept', responseType === 'json' ? 'application/json' : '*/*');
    }
    async parseResponse(response, responseType) {
        if (responseType === 'response') {
            return response;
        }
        if (responseType === 'text') {
            return await response.text();
        }
        if (responseType === 'blob') {
            return await response.blob();
        }
        if (responseType === 'arrayBuffer') {
            return await response.arrayBuffer();
        }
        if (responseType === 'formData') {
            return await response.formData();
        }
        if (response.status === 204) {
            return undefined;
        }
        const contentType = response.headers.get('content-type') ?? '';
        if (!contentType.includes('application/json')) {
            return undefined;
        }
        return await response.json();
    }
    async toAPIError(response) {
        const text = await response.clone().text();
        let body = text;
        try {
            body = JSON.parse(text);
        }
        catch {
            body = text;
        }
        return new HydraUIAPIError(response.status, response.statusText, body, response);
    }
}
export class HydraUIAPIClient extends APIClient {
    constructor(config = {}) {
        super(config);
        Object.assign(this, createClientHelpers(this));
    }
}
function parameterSection(params, section) {
    if (!params || typeof params !== 'object') {
        return {};
    }
    // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
    const value = params[section];
    // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
    return value && typeof value === 'object' ? value : {};
}
function joinURL(baseUrl, path) {
    if (!baseUrl) {
        return path;
    }
    return `${baseUrl}/${path.replace(/^\/+/, '')}`;
}
//# sourceMappingURL=client.js.map